import { Link, Text, View } from "@react-pdf/renderer";
import { useMemo } from "react";
import { chunk } from "../../../../../utils/chunk";
import { styles } from "./PDFCheckFruitTables";
import { PDFPage } from "./PDFLayout";
import { PDFTable, tableStyles } from "./PDFTable";
import { RootConfigType } from "../../../../../actions/Tenants/config/constantsTyped";

interface PDFPalletsProps {
    fields: any[];
    printChildren: any[];
    config: RootConfigType;
    children_title?: string;
    layer_fields?: any[];
    // destination for a pallet's label cell; pallets without one render as plain text
    getPalletLinkTarget?: (layerId: string) => string | undefined;
}

export function PDFPallets({ fields, printChildren, config, children_title, layer_fields, getPalletLinkTarget }: PDFPalletsProps) {
    const columnWeights = useMemo(
        () => fields.map((field) => field.field_config?.pdf_column_width_weight),
        [fields]
    );

    // stack the columns, starting with the headers, followed by content and finally the footer
    const headers = useMemo(() => fields.map((i, index) => (
        <View key={index} style={[tableStyles.tableCell, tableStyles.tableHeader, tableStyles.tableCellHeight]}>
            <Text style={tableStyles.tableHeaderText}>
                {i.label || "\u00A0"}
            </Text>
        </View>
    )), [fields]);

    const allColumns = useMemo(() => fields.map((i, index) => printChildren.map((p) => {
        const cell = i
            .clone()
            .set_value({
                config,
                layer: p,
                check: p.latest_check, // * To be used in check tables and layer overview
                fruit: null, // * Fruit list is not available on overview screen
                children: null, // * children is only available for layer index your are viewing
                form_options: config.form_options // * used to translate form values to labels

            })
            .display_vector(6, 50, 48);
        const linkTarget = i.fieldname_getter === "label" ? getPalletLinkTarget?.(p.id) : undefined;
        return (
            <View style={[tableStyles.tableCellHeight, tableStyles.tableCell]} key={index + 1}>
                {linkTarget ? <Link src={`#${linkTarget}`} style={tableStyles.link}>{cell}</Link> : cell}
            </View>
        );
    })), [fields, printChildren, config, getPalletLinkTarget]);


    // make chunks of all columns with 25 rows per column
    const chunked = useMemo(() => fields.map((_, index) => chunk(allColumns[index], 24)), [fields, allColumns]);

    return chunked[0].map((_, chunkIndex) => (
        <PDFPage footer key={chunkIndex}>
            {children_title && (
                <View>
                    <Text style={tableStyles.pageTitle}>{children_title}</Text>
                </View>
            )}

            {layer_fields && (
                <View style={styles.container}>
                    {layer_fields.map((i, index) => (
                        <View key={index} style={styles.metaItems}>
                            <View>
                                <Text>{i.label}:</Text>
                            </View>

                            <View style={{ ...styles.metaTextValueArtReport }}>
                                {i.display_vector(9)}
                            </View>
                        </View>
                    ))}
                </View>
            )}

            <PDFTable
                columns={chunked.map((i, colIndex) => [headers[colIndex], ...i[chunkIndex]])}
                columnWeights={columnWeights}
            />
        </PDFPage>
    ));
}
